import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Drawer from 'components/Drawer';
import Button from 'components/Button';
import { Calendar, Typography } from 'antd';
import { LeftOutlined, RightOutlined, CloseOutlined } from '@ant-design/icons';
import moment from 'moment';

const { Text } = Typography;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
  background: #ffffff;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const DrawerTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: #374151;
  }
`;

const DateSummary = styled.div`
  margin-bottom: 20px;
`;

const SummaryText = styled(Text)`
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
`;

const SelectedInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const SelectedText = styled(Text)`
  color: #374151;
  font-size: 14px;
  font-weight: 500;
`;

const ClearAllText = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  
  &:hover {
    color: #dc2626;
  }
`;

const MonthNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const MonthYear = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;

const CalendarGrid = styled.div`
  margin-bottom: 24px;
`;

const StyledCalendar = styled(Calendar)`
  .ant-picker-calendar-header {
    display: none;
  }
  
  .ant-picker-cell {
    padding: 0;
    border: none !important;
  }
  
  .ant-picker-cell-inner {
    padding: 0;
    border: none !important;
    background: transparent !important;
    border-radius: 0;
    height: 40px;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .ant-picker-calendar-date {
    border: none !important;
    background: transparent !important;
    height: 40px;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 0;
  }
  
  .ant-picker-calendar-date-today {
    border: none !important;
    background: transparent !important;
  }
  
  .ant-picker-calendar-date-value {
    display: none;
  }
  
  .ant-picker-calendar-date-content {
    height: 40px;
    width: 40px;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  /* Remove all hover effects */
  .ant-picker-cell:hover .ant-picker-cell-inner {
    background: transparent !important;
  }
  
  .ant-picker-calendar-date:hover {
    background: transparent !important;
  }
`;

const ErrorNotification = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
`;

// Remove the fixed footer and use regular container
const CalendarContainer = styled.div`
   padding: 10px;
`;

const ErrorText = styled(Text)`
  color: #991b1b;
  font-size: 14px;
`;

// Footer buttons styled like other drawers
const FooterButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const CustomDateCalendarDrawer = ({ 
  visible, 
  onClose, 
  startDate, 
  endDate, 
  selectedDates = [], 
  onDatesChange,
  minDates = 6,
  isViewOnly = false, // Add this prop
  title = "Calendar View" // Add custom title prop
}) => {
  const [tempSelectedDates, setTempSelectedDates] = useState([...selectedDates]);
  const [currentMonth, setCurrentMonth] = useState(moment());

  useEffect(() => {
    setTempSelectedDates([...selectedDates]);
  }, [selectedDates]);

  useEffect(() => {
    // Set current month to the start date when drawer opens
    if (visible && startDate) {
      // Convert string date to moment object if it's not already
      const momentStartDate = typeof startDate === 'string' ? moment(startDate) : startDate;
      setCurrentMonth(momentStartDate);
    }
  }, [visible, startDate]);

  const handleDateSelect = (date) => {
    // Don't allow date selection in view-only mode
    if (isViewOnly) return;
    
    const dateStr = date.format('YYYY-MM-DD');
    
    // Check if date is within range
    if (!startDate || !endDate || date.isBefore(startDate, 'day') || date.isAfter(endDate, 'day')) {
      return;
    }

    setTempSelectedDates(prev => {
      if (prev.includes(dateStr)) {
        return prev.filter(d => d !== dateStr);
      } else {
        return [...prev, dateStr].sort();
      }
    });
  };

  const handleClearAll = () => {
    // Don't allow clearing in view-only mode
    if (isViewOnly) return;
    setTempSelectedDates([]);
  };

  const handleCancel = () => {
    if (!isViewOnly) {
      setTempSelectedDates([...selectedDates]); // Reset to original selection
    }
    onClose();
  };

  const handleUpdate = () => {
    if (isValidSelection && !isViewOnly) {
      onDatesChange(tempSelectedDates);
      onClose();
    }
  };

  const dateCellRender = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    const isSelected = tempSelectedDates.includes(dateStr);
    const isInRange = startDate && endDate && date.isSameOrAfter(startDate, 'day') && date.isSameOrBefore(endDate, 'day');
    const isDisabled = !isInRange;
    const isToday = date.isSame(moment(), 'day');

    let bgColor = 'transparent';
    if (isSelected) {
      bgColor = isViewOnly ? '#10b981' : '#6b7280'; // Green for scheduled dates in view mode
    } else if (isToday && isInRange) {
      bgColor = '#e0e7ff';
    }

    let color = isSelected ? 'white' : isDisabled ? '#d1d5db' : '#374151';

    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isViewOnly ? 'default' : (isDisabled ? 'not-allowed' : 'pointer'),
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (!isDisabled && !isViewOnly) {
            handleDateSelect(date);
          }
        }}
      >
        <div
          style={{
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: bgColor,
            color,
            borderRadius: '50%',
            fontSize: '14px',
            fontWeight: isSelected ? '600' : '400',
            opacity: isDisabled ? 0.5 : 1,
            transition: 'all 0.2s ease',
          }}
        >
          {date.date()}
        </div>
      </div>
    );
  };

  const isValidSelection = tempSelectedDates.length >= minDates;
  
  const formatSelectedDates = () => {
    return tempSelectedDates.map(date => {
      const momentDate = moment(date);
      return momentDate.format('MMMM Do');
    }).join(', ');
  };

  return (
    <Drawer
      title=""
      placement="right"
      width="40%"
      padding="0px"
      onClose={handleCancel}
      visible={visible}
      closable={false}
      content={
        <>
          <DrawerHeader>
            <DrawerTitle>{title}</DrawerTitle>
            <CloseButton onClick={handleCancel}>
              <CloseOutlined size={20} />
            </CloseButton>
          </DrawerHeader>
          
          <CalendarContainer>
            <DateSummary>
              <SummaryText>
                {isViewOnly 
                  ? `Scheduled assessment dates from ${typeof startDate === 'string' ? moment(startDate).format('DD/MM/YYYY') : startDate?.format('DD/MM/YYYY')} to ${typeof endDate === 'string' ? moment(endDate).format('DD/MM/YYYY') : endDate?.format('DD/MM/YYYY')}.`
                  : `Showing assmt. start date from ${typeof startDate === 'string' ? moment(startDate).format('DD/MM/YYYY') : startDate?.format('DD/MM/YYYY')} to ${typeof endDate === 'string' ? moment(endDate).format('DD/MM/YYYY') : endDate?.format('DD/MM/YYYY')}.`
                }
              </SummaryText>
              <br />
              <SummaryText>
                {isViewOnly 
                  ? `${tempSelectedDates.length} assessment dates scheduled.`
                  : `Select up to ${minDates} dates.`
                }
              </SummaryText>
            </DateSummary>

            {/* <SelectedInfo>
              <SelectedText>
                {isViewOnly 
                  ? `${tempSelectedDates.length} scheduled dates`
                  : `Selected ${tempSelectedDates.length} of ${minDates}`
                }
              </SelectedText>
              {tempSelectedDates.length > 0 && !isViewOnly && (
                <ClearAllText onClick={handleClearAll}>
                  Clear all
                </ClearAllText>
              )}
            </SelectedInfo> */}

            <MonthNavigation>
              <NavButton onClick={() => setCurrentMonth(prev => prev.clone().subtract(1, 'month'))}>
                <LeftOutlined />
              </NavButton>
              <MonthYear>
                {currentMonth.format('MMMM YYYY')}
              </MonthYear>
              <NavButton onClick={() => setCurrentMonth(prev => prev.clone().add(1, 'month'))}>
                <RightOutlined />
              </NavButton>
            </MonthNavigation>

            <CalendarGrid>
              <StyledCalendar
                value={currentMonth}
                fullscreen={false}
                dateCellRender={dateCellRender}
                disabledDate={(date) => {
                  return !startDate || !endDate || date.isBefore(startDate, 'day') || date.isAfter(endDate, 'day');
                }}
              />
            </CalendarGrid>

            {!isViewOnly && !isValidSelection && tempSelectedDates.length > 0 && (
              <ErrorNotification>
                <ErrorText>
                  You've selected only {tempSelectedDates.length} dates ({formatSelectedDates()}). 
                  Please select {minDates - tempSelectedDates.length} more to continue
                </ErrorText>
              </ErrorNotification>
            )}

            {/* Show schedule summary in view-only mode */}
            {/* {isViewOnly && tempSelectedDates.length > 0 && (
              <div style={{
                background: '#f0f9ff',
                border: '1px solid #0ea5e9',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '20px',
              }}>
                <Text style={{ color: '#0369a1', fontSize: '14px', fontWeight: '500' }}>
                  Scheduled Assessment Dates:
                </Text>
                <br />
                <Text style={{ color: '#0369a1', fontSize: '14px' }}>
                  {formatSelectedDates()}
                </Text>
              </div>
            )} */}
          </CalendarContainer>
        </>
      }
      footer={
        isViewOnly ? (
          <FooterButtons>
            <Button.Primary 
              text="Close" 
              onClick={handleCancel}
            />
          </FooterButtons>
        ) : (
          <FooterButtons>
            <Button.Secondary text="Cancel" onClick={handleCancel} />
            <Button.Primary 
              text="Update" 
              onClick={handleUpdate} 
              disabled={!isValidSelection}
            />
          </FooterButtons>
        )
      }
    />
  );
};

export default CustomDateCalendarDrawer;